'use client';

import { useState, useEffect } from 'react';
import { ProductCard } from './productCard';

const BREAKPOINTS = [
    { query: '(max-width: 575px)', count: 1 },
    { query: '(max-width: 767px)', count: 2 },
];

function useVisibleCount() {
    const [count, setCount] = useState(3);

    useEffect(() => {
        const update = () => {
            for (const { query, count: c } of BREAKPOINTS) {
                if (window.matchMedia(query).matches) { setCount(c); return; }
            }
            setCount(3);
        };
        update();
        const mqs = BREAKPOINTS.map(({ query }) => {
            const mq = window.matchMedia(query);
            mq.addEventListener('change', update);
            return mq;
        });
        return () => mqs.forEach(mq => mq.removeEventListener('change', update));
    }, []);

    return count;
}

const NAV_BTN = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 2,
    width: 34,
    height: 34,
    borderRadius: '50%',
    background: '#fff',
    border: '1px solid #e2e8f0',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    padding: 0,
};

export const ProductSlider = ({ data = [] }) => {
    const [idx, setIdx] = useState(0);
    const visibleCount = useVisibleCount();
    const maxIdx = Math.max(0, data.length - visibleCount);

    // Clamp idx when visible count changes on resize
    useEffect(() => {
        setIdx(i => Math.min(i, Math.max(0, data.length - visibleCount)));
    }, [visibleCount, data.length]);

    if (!data.length) return null;

    return (
        <div style={{ position: 'relative', overflow: 'hidden' }}>
            {/* Track: no DOM reads — position driven purely by CSS calc */}
            <div
                style={{
                    display: 'flex',
                    flexWrap: 'nowrap',
                    transform: `translateX(calc(${-idx} * 100% / ${visibleCount}))`,
                    transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    willChange: 'transform',
                }}
            >
                {data.map((item) => (
                    <div
                        key={item._id || item.id || item.slug}
                        style={{
                            flex: `0 0 calc(100% / ${visibleCount})`,
                            minWidth: 0,
                            padding: '0 12px',
                            boxSizing: 'border-box',
                        }}
                    >
                        <ProductCard data={item} />
                    </div>
                ))}
            </div>

            {idx > 0 && (
                <button
                    onClick={() => setIdx(i => Math.max(0, i - 1))}
                    aria-label="Previous products"
                    style={{ ...NAV_BTN, left: 4 }}
                >
                    <i className="pi pi-chevron-left" style={{ fontSize: '0.9rem', color: '#0c3264' }} />
                </button>
            )}
            {idx < maxIdx && (
                <button
                    onClick={() => setIdx(i => Math.min(maxIdx, i + 1))}
                    aria-label="Next products"
                    style={{ ...NAV_BTN, right: 4 }}
                >
                    <i className="pi pi-chevron-right" style={{ fontSize: '0.9rem', color: '#0c3264' }} />
                </button>
            )}
        </div>
    );
};
